import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useReduxAuth';
import { useNotification } from './index';
import { useAccessibleFirmIds } from './useAccessibleFirmIds';
import { usePermissions } from './usePermissions';
import { SCREENS, ACTIONS } from '../constants/permissions';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_ALERT_INSTANCES,
  GET_ALERT_CONFIGS,
  GET_MY_DISMISSALS,
} from '../graphql/queries/alerts';
import {
  UPDATE_ALERT_CONFIG,
  UPDATE_ALERT_TYPE_SEVERITY,
  ADD_ALERT_CONFIG_TARGET,
  DELETE_ALERT_CONFIG_TARGET,
  DISMISS_ALERT,
  UNDISMISS_ALERT,
} from '../graphql/mutations/alerts';

const CATEGORY_TO_SCREEN = {
  CONTRACTS: SCREENS.CONTRACTS,
  OFFERS: SCREENS.OFFERS,
  JOBS: SCREENS.JOB_MANAGEMENT,
  INVOICES: SCREENS.INVOICE_MANAGEMENT,
};

const filterInstances = (instances, configs, user, firmIds, hasPermission) => {
  if (user?.role === 'ROOT') return instances;

  const userId = parseInt(user?.id);
  const teamId = user?.team_id ? parseInt(user.team_id) : null;

  return instances.filter(inst => {
    const cfg = configs.find(c => c.alert_type_code === inst.alert_type_code);
    if (!cfg) return false;

    // 0. Kategori → ekran izin kontrolü (ör. faturayı görme yetkisi yoksa fatura uyarısı gösterme)
    const screen = CATEGORY_TO_SCREEN[inst.alert_type_config?.category];
    if (screen && !hasPermission(screen, ACTIONS.VIEW)) return false;

    const targets = cfg.alert_config_targets || [];

    // 1. Hedef kontrolü: hedef varsa kullanıcı/takım içinde olmalı
    if (targets.length > 0) {
      const inTargets = targets.some(t => {
        if (t.target_type === 'user') return parseInt(t.target_value) === userId;
        if (t.target_type === 'team') return teamId !== null && parseInt(t.target_value) === teamId;
        return false;
      });
      if (!inTargets) return false;
      return true; // hedefte → göster
    }

    // 2. Hedefsiz: firma bazlı filtre (firm_id olmalı)
    if (inst.firm_id === null) return false; // firm_id yok + hedef yok → gizle
    if (firmIds === null) return true; // kısıtsız rol
    return firmIds.includes(inst.firm_id);
  });
};

export const useAlerts = () => {
  const { user } = useAuth();
  const { firmIds, loading: firmsLoading } = useAccessibleFirmIds();
  const { hasPermission } = usePermissions();

  const [instances, setInstances] = useState([]);
  const [dismissedInstances, setDismissedInstances] = useState([]);
  const [dismissedIds, setDismissedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (showLoading = true) => {
    if (!user?.id || !user?.companyId || firmsLoading) return;
    try {
      if (showLoading) setLoading(true);
      const [instResult, cfgResult, dismissResult] = await Promise.all([
        hasuraClient.query(GET_ALERT_INSTANCES, { companyId: parseInt(user.companyId) }),
        hasuraClient.query(GET_ALERT_CONFIGS, {}),
        hasuraClient.query(GET_MY_DISMISSALS, { userId: parseInt(user.id) }),
      ]);
      const all = instResult.alert_instances || [];
      const configs = cfgResult.alert_configs || [];
      const dismissals = dismissResult.alert_dismissals || [];
      const dismissSet = new Set(dismissals.map(d => d.alert_instance_id));

      // Pasife cekilmis config'lerin (`is_enabled = false`) mevcut
      // instance'larini da listeden dus. Cron artik yeni uretmiyor ama DB'de
      // duran eskiler `is_resolved = false` oldugu icin listelenmeye devam
      // ediyordu (bug: "iki uyariyi pasif yaptim, yenileyince hala
      // gorunuyor"). alert_type_code uzerinden eslesme yapariz.
      const enabledCodes = new Set(
        configs.filter(c => c.is_enabled).map(c => c.alert_type_code),
      );
      const configEnabledFiltered = all.filter(i => enabledCodes.has(i.alert_type_code));

      const filtered = filterInstances(configEnabledFiltered, configs, user, firmIds, hasPermission);
      setDismissedIds(dismissSet);
      setInstances(filtered.filter(i => !dismissSet.has(i.id)));
      setDismissedInstances(filtered.filter(i => dismissSet.has(i.id)));
    } catch {
      // silent fail
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.companyId, user?.role, user?.team_id, firmIds, firmsLoading, hasPermission]);

  const fetchInstances = useCallback(() => fetchData(true), [fetchData]);

  useEffect(() => { fetchInstances(); }, [fetchInstances]);

  const dismissAlert = useCallback(async (alertInstanceId) => {
    if (!user?.id) return;
    // Optimistic: anında listeden kaldır
    setInstances(prev => {
      const item = prev.find(i => i.id === alertInstanceId);
      if (item) setDismissedInstances(d => [...d, item]);
      return prev.filter(i => i.id !== alertInstanceId);
    });
    try {
      await hasuraClient.mutate(DISMISS_ALERT, {
        object: { alert_instance_id: alertInstanceId, user_id: parseInt(user.id) },
      });
      await fetchData(false);
    } catch {
      await fetchData(false); // hata olursa geri al
    }
  }, [user?.id, fetchData]);

  const undismissAlert = useCallback(async (alertInstanceId) => {
    if (!user?.id) return;
    // Optimistic: anında dismissed listeden kaldır
    setDismissedInstances(prev => {
      const item = prev.find(i => i.id === alertInstanceId);
      if (item) setInstances(d => [...d, item]);
      return prev.filter(i => i.id !== alertInstanceId);
    });
    try {
      await hasuraClient.mutate(UNDISMISS_ALERT, {
        alertInstanceId,
        userId: parseInt(user.id),
      });
      await fetchData(false);
    } catch {
      await fetchData(false);
    }
  }, [user?.id, fetchData]);

  const byCategory = instances.reduce((acc, inst) => {
    const cat = inst.alert_type_config?.category || 'OTHER';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(inst);
    return acc;
  }, {});

  return {
    instances,
    dismissedInstances,
    dismissedIds,
    loading,
    byCategory,
    fetchInstances,
    dismissAlert,
    undismissAlert,
  };
};

export const useAlertConfigs = () => {
  const { showNotification } = useNotification();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchConfigs = useCallback(async () => {
    try {
      setLoading(true);
      const result = await hasuraClient.query(GET_ALERT_CONFIGS, {});
      setConfigs(result.alert_configs || []);
    } catch {
      showNotification('Uyarı yapılandırmaları yüklenemedi', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchConfigs(); }, [fetchConfigs]);

  const updateConfig = useCallback(async (id, set) => {
    try {
      const result = await hasuraClient.mutate(UPDATE_ALERT_CONFIG, { id, set });
      const updated = result.update_alert_configs_by_pk;
      setConfigs(prev => prev.map(c => c.id === id ? { ...c, ...updated } : c));
      showNotification('Yapılandırma güncellendi', 'success');
    } catch {
      showNotification('Yapılandırma güncellenemedi', 'error');
    }
  }, [showNotification]);

  const addTarget = useCallback(async (configId, targetType, targetValue) => {
    try {
      const result = await hasuraClient.mutate(ADD_ALERT_CONFIG_TARGET, {
        object: { config_id: configId, target_type: targetType, target_value: targetValue },
      });
      const newTarget = result.insert_alert_config_targets_one;
      setConfigs(prev => prev.map(c =>
        c.id === configId
          ? { ...c, alert_config_targets: [...(c.alert_config_targets || []), newTarget] }
          : c
      ));
    } catch {
      showNotification('Hedef eklenemedi', 'error');
    }
  }, [showNotification]);

  const removeTarget = useCallback(async (configId, targetId) => {
    try {
      await hasuraClient.mutate(DELETE_ALERT_CONFIG_TARGET, { id: targetId });
      setConfigs(prev => prev.map(c =>
        c.id === configId
          ? { ...c, alert_config_targets: (c.alert_config_targets || []).filter(t => t.id !== targetId) }
          : c
      ));
    } catch {
      showNotification('Hedef kaldırılamadı', 'error');
    }
  }, [showNotification]);

  const updateSeverity = useCallback(async (code, severity) => {
    try {
      await hasuraClient.mutate(UPDATE_ALERT_TYPE_SEVERITY, { code, severity });
      setConfigs(prev => prev.map(c =>
        c.alert_type_code === code
          ? { ...c, alert_type_config: { ...c.alert_type_config, severity } }
          : c
      ));
    } catch {
      showNotification('Önem seviyesi güncellenemedi', 'error');
    }
  }, [showNotification]);

  return { configs, loading, fetchConfigs, updateConfig, updateSeverity, addTarget, removeTarget };
};
